/**
 * Tiempos de entrega (mediana y percentil 90) de los cinco tramos. Cada renglón dice cuántos se
 * midieron con la hora exacta que informa Twilio (Event Streams) y cuántos con la hora en que nos
 * llegó el aviso: son relojes distintos y el operador tiene que saber cuál está leyendo.
 *
 * A una persona de un cliente (Accesos, rol «comercio») no se le nombra a Twilio: la tarjeta sale en
 * Estadísticas y en el detalle de su campaña, y decía «3 de 3 con hora de Twilio» y «desde que Deenex le
 * pasó el mensaje a Twilio». Ve cuántos mensajes se midieron y una aclaración con WhatsApp
 * (`ACLARACION_TIEMPOS_COMERCIO`); el equipo, lo de siempre.
 */

import type { Tiempos, Percentiles } from "../../types.estadisticas";
import { ACLARACION_TIEMPOS_COMERCIO, TRAMOS, aclaracionTiempos, duracionTexto } from "../../logic/estadisticas";
import { num, plural } from "../../../campana-nueva/utils/format";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

export function TarjetaTiempos({ tiempos }: { tiempos: Tiempos | null | undefined }) {
  const equipo = useEsEquipoDeenex();
  return (
    <div className="space-y-3">
      <ul className="divide-y divide-slate-100">
        {TRAMOS.map(({ clave, titulo }) => {
          const p = tiempos?.[clave] as Percentiles | undefined;
          const n = Number(p?.n) || 0;
          return (
            <li key={clave} className="py-2.5 flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{titulo}</p>
                <p className="text-[11px] text-slate-400">
                  {!n ? "Sin mediciones"
                    : equipo ? `${num(Math.min(Number(p?.conHoraTwilio) || 0, n))} de ${num(n)} con hora de Twilio`
                    : `${num(n)} ${plural(n, "mensaje medido", "mensajes medidos")}`}
                </p>
              </div>
              {n > 0 && (
                <p className="text-sm text-slate-700 text-right">
                  Mediana <strong className="text-slate-900">{duracionTexto(p?.p50Seg)}</strong>
                  <span className="block text-xs text-slate-500">9 de cada 10 en menos de {duracionTexto(p?.p90Seg)}</span>
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {tiempos?.muestreado && <p className="text-xs text-amber-700">Hay muchos mensajes en el rango: se midieron los 50.000 más recientes.</p>}
      {/* La aclaración la arma el backend contando MENSAJES (los que tuvieron alguna hora de Twilio
          sobre los medidos). Recalcularla acá con los cinco tramos daba otro número: con 100
          mensajes decía «… de 430», y los tramos que arrancan en la llamada a la API nunca tienen
          hora de Twilio en las dos puntas. El cálculo local queda sólo si el backend no la manda.
          A una persona de un cliente, la de WhatsApp: la del backend nombra a Twilio. */}
      <p className="text-xs text-slate-400 leading-relaxed">
        {equipo ? String(tiempos?.aclaracion ?? "").trim() || aclaracionTiempos(tiempos) : ACLARACION_TIEMPOS_COMERCIO}
      </p>
    </div>
  );
}

export default TarjetaTiempos;
